"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Menu,
  X,
  Search,
  ShoppingCart,
  User,
  Heart,
  ChevronDown,
  LogOut,
  Settings,
  Package,
  Grid3x3,
} from "lucide-react";
import {
  GET_PRODUCT_CATEGORIES,
  GET_PRODUCT_SUBCATEGORIES,
} from "@/services/ApiRoutes";
import { get, logout } from "@/services/ApiService";
import { Category, SubCategory } from "@/types/shop";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { fetchWishlist } from "@/store/slices/wishlistSlice";
import { getValidImageUrl } from "@/utils/imageUtils";
import AuthDrawer, { AuthDrawerMode } from "@/components/shop/AuthDrawer";
import AlertDialog from "@/utils/AlertDialog";

interface ShopHeaderProps {
  categories?: Category[];
}

const ShopHeader = ({
  categories: initialCategories,
}: ShopHeaderProps = {}) => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const router = useRouter();

  const handleLogoutConfirm = () => {
    setShowUserMenu(false);
    setIsOpen(false);
    AlertDialog(
      "Confirm Logout",
      "Are you sure you want to sign out of your account?",
      "warning",
      0,
      true,
      true,
      "Yes, Logout",
      "Cancel",
      async () => {
        await logout();
      },
    );
  };
  const [categories, setCategories] = useState<Category[]>(
    initialCategories || [],
  );
  const [isVisible, setIsVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [isAuthDrawerOpen, setIsAuthDrawerOpen] = useState(false);
  const [authDrawerMode, setAuthDrawerMode] = useState<AuthDrawerMode>("login");
  const topBarsRef = React.useRef<HTMLDivElement>(null);
  const [topBarsHeight, setTopBarsHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (topBarsRef.current) {
        setTopBarsHeight(topBarsRef.current.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [mounted, currentAnnouncement]);

  useEffect(() => {
    dispatch(fetchWishlist());
  }, [dispatch]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateScrollDir = () => {
      const scrollY = window.scrollY;

      // When at the very top of the page
      if (scrollY <= 8) {
        setIsVisible(true);
        setScrolled(false);
        lastScrollY = scrollY > 0 ? scrollY : 0;
        ticking = false;
        return;
      }

      setScrolled(true);

      const diff = scrollY - lastScrollY;
      if (Math.abs(diff) >= 3) {
        const isScrollingDown = diff > 0;
        setIsVisible(!isScrollingDown);

        if (isScrollingDown) {
          setShowUserMenu(false);
          setShowCategories(false);
        }
        lastScrollY = scrollY;
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleOpenAuth = (e: any) => {
      if (e.detail?.mode) {
        setAuthDrawerMode(e.detail.mode);
      }
      setIsAuthDrawerOpen(true);
    };
    window.addEventListener(
      "open-auth-drawer",
      handleOpenAuth as EventListener,
    );
    return () =>
      window.removeEventListener(
        "open-auth-drawer",
        handleOpenAuth as EventListener,
      );
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setShowMobileCategories(false);
  }, [pathname]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, subRes] = await Promise.all([
          get(`${GET_PRODUCT_CATEGORIES}?all=true`),
          get(`${GET_PRODUCT_SUBCATEGORIES}?all=true`),
        ]);

        if (catRes?.success && subRes?.success) {
          const cats: Category[] = catRes.data || [];
          const subs: SubCategory[] = subRes.data || [];

          const categoriesWithSubs = cats.map((cat) => {
            const matchedSubs = subs.filter((sub) => {
              const subCatId =
                typeof sub.category === "object"
                  ? (sub.category as any)?._id
                  : sub.category;
              return String(subCatId) === String(cat._id);
            });

            const finalSubs =
              matchedSubs.length > 0 ? matchedSubs : cat.subCategories || [];

            return {
              ...cat,
              subCategories: finalSubs.map((sub) => ({
                ...sub,
                productCount: sub.productCount ?? 0,
              })),
            };
          });

          setCategories(categoriesWithSubs);
          if (categoriesWithSubs.length > 0) {
            setActiveCategory(categoriesWithSubs[0]._id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch header data:", error);
      }
    };
    fetchData();
  }, []);

  const announcements = [
    "Welcome to Hubmicroo Shop - Your One Stop Solution",
    "Free Shipping on Orders Over $50",
    "New Arrivals: Check out the latest summer collection",
    "24/7 Customer Support Available",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [announcements.length]);

  const cartCount = useAppSelector((state) => state.cart.itemCount);
  const wishlistCount = useAppSelector((state) => state.wishlist.items.length);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest("[data-user-menu]") &&
        !target.closest("[data-categories-menu]")
      ) {
        setShowUserMenu(false);
        setShowCategories(false);
      }
    };

    if (showUserMenu || showCategories) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showUserMenu, showCategories]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!mounted) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white h-8 sm:h-10" />
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 sm:h-20 flex justify-between items-center">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 shadow-sm">
            <Grid3x3 size={16} className="text-white" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header
        style={{
          transform: isVisible
            ? "translateY(0)"
            : `translateY(-${topBarsHeight || 116}px)`,
          transition:
            "transform 0.22s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.2s ease, box-shadow 0.2s ease",
        }}
        className={`fixed top-0 left-0 right-0 w-full z-50 will-change-transform ${
          scrolled || !isVisible
            ? "bg-white/95 backdrop-blur-md shadow-md"
            : "bg-white shadow-sm"
        }`}
      >
        {/* Top 2 Navigation Bars Container (Slides up on scroll down) */}
        <div ref={topBarsRef} className="w-full bg-white">
          {/* 1. Top Announcement Bar */}
          <div className="overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <div className="mx-auto px-3 sm:px-6 lg:px-8 max-w-7xl">
              <div className="flex justify-between items-center gap-2 h-8 sm:h-9 text-xs sm:text-sm">
                <div className="flex-1 flex justify-center md:justify-start min-w-0">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentAnnouncement}
                      initial={{ y: 15, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -15, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="font-medium truncate"
                    >
                      {announcements[currentAnnouncement]}
                    </motion.p>
                  </AnimatePresence>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 text-xs sm:text-sm">
                  <Link
                    href="/track-order"
                    className="hover:underline transition-opacity hover:opacity-80 font-medium"
                  >
                    Track
                  </Link>
                  <span className="text-white/60 select-none">|</span>
                  <Link
                    href="/help"
                    className="hover:underline transition-opacity hover:opacity-80 font-medium"
                  >
                    Help
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Main Header */}
          <div className="mx-auto px-3 sm:px-6 lg:px-8 max-w-7xl">
            <div className="flex justify-between items-center h-16 sm:h-20">
              {/* Logo */}
              <Link href="/shop">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center space-x-2 cursor-pointer flex-shrink-0"
                >
                  <div className="flex items-center gap-2 px-1 sm:px-2 h-8 sm:h-10">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex-shrink-0 shadow-sm">
                      <Grid3x3 size={14} className="text-white" />
                    </div>
                  </div>
                </motion.div>
              </Link>

              {/* Search Bar - Hidden on mobile, visible on tablet+ */}
              <div className="hidden md:flex flex-1 mx-4 lg:mx-8 max-w-md lg:max-w-xl xl:max-w-2xl">
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchQuery.trim()) {
                        router.push(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
                      }
                    }}
                    className="py-2 lg:py-2.5 pr-3 lg:pr-4 pl-9 lg:pl-11 border-2 border-gray-200 hover:border-purple-300 focus:border-purple-500 focus:outline-none rounded-lg w-full transition-colors text-sm bg-gray-50 focus:bg-white"
                    aria-label="Search products"
                  />
                  <Search
                    className="top-1/2 left-3 lg:left-4 absolute text-gray-400 -translate-y-1/2 pointer-events-none"
                    size={18}
                  />
                </div>
              </div>

              {/* Actions - Desktop view */}
              <div className="hidden lg:flex items-center gap-4">
                {/* Wishlist - Desktop */}
                <Link href="/wishlist">
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors flex items-center justify-center text-gray-700 hover:text-purple-600"
                    aria-label="Wishlist"
                  >
                    <Heart size={22} />
                    {wishlistCount > 0 && (
                      <span className="-top-1 -right-1 absolute flex justify-center items-center bg-red-500 rounded-full w-5 h-5 text-white text-xs font-bold shadow-xs">
                        {wishlistCount}
                      </span>
                    )}
                  </motion.button>
                </Link>

                {/* Cart - Desktop */}
                <Link href="/cart">
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors flex items-center justify-center text-gray-700 hover:text-purple-600"
                    aria-label="Shopping cart"
                  >
                    <ShoppingCart size={22} />
                    {cartCount > 0 && (
                      <span className="-top-1 -right-1 absolute flex justify-center items-center bg-purple-600 rounded-full w-5 h-5 text-white text-xs font-bold shadow-xs">
                        {cartCount}
                      </span>
                    )}
                  </motion.button>
                </Link>

                {/* User Profile - Desktop */}
                <div className="relative" data-user-menu>
                  {user ? (
                    <>
                      <motion.button
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowUserMenu(!showUserMenu);
                        }}
                        className="relative cursor-pointer hover:bg-purple-50 p-1.5 rounded-full transition-colors flex items-center justify-center"
                        aria-label="User profile menu"
                        title={user?.name || "Account Profile"}
                      >
                        <div className="flex justify-center items-center bg-gradient-to-br from-purple-600 to-pink-600 rounded-full w-8 h-8 overflow-hidden ring-2 ring-purple-500/20 shadow-xs">
                          {user?.profileImage ? (
                            <img
                              src={
                                getValidImageUrl(user.profileImage) || undefined
                              }
                              alt={user?.name}
                              className="rounded-full w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-xs font-bold uppercase">
                              {user?.name ? (
                                user.name.charAt(0)
                              ) : (
                                <User className="text-white" size={16} />
                              )}
                            </span>
                          )}
                        </div>
                      </motion.button>

                      <AnimatePresence>
                        {showUserMenu && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            onClick={(e) => e.stopPropagation()}
                            className="right-0 absolute bg-white shadow-xl mt-2 border border-gray-100 rounded-lg w-56 overflow-hidden z-50"
                          >
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-gray-100 border-b">
                              <p className="font-semibold text-gray-800">
                                {user?.name}
                              </p>
                              <p className="text-gray-600 text-sm">
                                {user?.email}
                              </p>
                            </div>
                            <div className="py-2">
                              <Link href="/profile">
                                <button className="flex cursor-pointer items-center gap-3 hover:bg-purple-50 px-4 py-2 w-full text-gray-700 text-left text-sm transition-colors">
                                  <User size={18} />
                                  My Profile
                                </button>
                              </Link>
                              <Link href="/orders">
                                <button className="flex cursor-pointer items-center gap-3 hover:bg-purple-50 px-4 py-2 w-full text-gray-700 text-left text-sm transition-colors">
                                  <Package size={18} />
                                  My Orders
                                </button>
                              </Link>
                              <Link href="/wishlist">
                                <button className="flex cursor-pointer items-center gap-3 hover:bg-purple-50 px-4 py-2 w-full text-gray-700 text-left text-sm transition-colors">
                                  <Heart size={18} />
                                  Wishlist
                                </button>
                              </Link>
                              <Link href="/settings">
                                <button className="flex cursor-pointer items-center gap-3 hover:bg-purple-50 px-4 py-2 w-full text-gray-700 text-left text-sm transition-colors">
                                  <Settings size={18} />
                                  Settings
                                </button>
                              </Link>
                              <hr className="my-2" />
                              <button
                                onClick={handleLogoutConfirm}
                                className="flex cursor-pointer items-center gap-3 hover:bg-red-50 px-4 py-2 w-full text-red-600 text-left text-sm transition-colors"
                              >
                                <LogOut size={18} />
                                Logout
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setAuthDrawerMode("login");
                        setIsAuthDrawerOpen(true);
                      }}
                      className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors flex items-center justify-center text-gray-700 hover:text-purple-600"
                      aria-label="Login"
                      title="Sign In / Register"
                    >
                      <User size={22} />
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Tablet view - compact icons */}
              <div className="hidden md:flex lg:hidden items-center gap-2 sm:gap-3">
                {/* Wishlist - Tablet */}
                <Link href="/wishlist">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors"
                    aria-label="Wishlist"
                  >
                    <Heart className="text-gray-700" size={20} />
                    {wishlistCount > 0 && (
                      <span className="-top-1 -right-1 absolute flex justify-center items-center bg-red-500 rounded-full w-4 h-4 text-white text-xs font-bold">
                        {wishlistCount}
                      </span>
                    )}
                  </motion.button>
                </Link>

                {/* Cart - Tablet */}
                <Link href="/cart">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors"
                    aria-label="Shopping cart"
                  >
                    <ShoppingCart className="text-gray-700" size={20} />
                    {cartCount > 0 && (
                      <span className="-top-1 -right-1 absolute flex justify-center items-center bg-purple-600 rounded-full w-4 h-4 text-white text-xs font-bold">
                        {cartCount}
                      </span>
                    )}
                  </motion.button>
                </Link>

                {/* User - Tablet */}
                {user ? (
                  <Link href="/profile">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-1 rounded-full hover:bg-purple-50 transition-colors flex items-center justify-center"
                      aria-label="User profile"
                    >
                      <div className="flex justify-center items-center bg-gradient-to-br from-purple-600 to-pink-600 rounded-full w-7 h-7 overflow-hidden ring-2 ring-purple-500/20">
                        {user?.profileImage ? (
                          <img
                            src={
                              getValidImageUrl(user.profileImage) || undefined
                            }
                            alt={user?.name}
                            className="rounded-full w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white text-[10px] font-bold uppercase">
                            {user?.name ? (
                              user.name.charAt(0)
                            ) : (
                              <User className="text-white" size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </motion.button>
                  </Link>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setAuthDrawerMode("login");
                      setIsAuthDrawerOpen(true);
                    }}
                    className="p-2 rounded-full hover:bg-purple-50 transition-colors cursor-pointer text-gray-700 hover:text-purple-600"
                    aria-label="User login"
                  >
                    <User size={20} />
                  </motion.button>
                )}

                {/* Mobile Menu Toggle */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                  className="p-2 rounded-lg text-gray-700 ml-1 hover:bg-purple-50 transition-colors cursor-pointer"
                  type="button"
                  aria-label="Toggle menu"
                >
                  {isOpen ? <X size={24} /> : <Menu size={24} />}
                </motion.button>
              </div>

              {/* Mobile view - simplified */}
              <div className="flex md:hidden items-center gap-1.5 sm:gap-2">
                {/* Wishlist - Mobile */}
                <Link href="/wishlist">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors flex items-center justify-center text-gray-700 hover:text-purple-600"
                    aria-label="Wishlist"
                  >
                    <Heart size={20} />
                    {wishlistCount > 0 && (
                      <span className="-top-1 -right-1 absolute flex justify-center items-center bg-red-500 rounded-full w-4 h-4 text-white text-[10px] font-bold shadow-xs">
                        {wishlistCount}
                      </span>
                    )}
                  </motion.button>
                </Link>

                {/* Cart - Mobile */}
                <Link href="/cart">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative cursor-pointer hover:bg-purple-50 p-2 rounded-full transition-colors flex items-center justify-center text-gray-700 hover:text-purple-600"
                    aria-label="Shopping cart"
                  >
                    <ShoppingCart size={20} />
                    {cartCount > 0 && (
                      <span className="-top-1 -right-1 absolute flex justify-center items-center bg-purple-600 rounded-full w-4 h-4 text-white text-[10px] font-bold shadow-xs">
                        {cartCount}
                      </span>
                    )}
                  </motion.button>
                </Link>

                {/* Mobile Menu Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                  }}
                  className="p-2 rounded-lg text-gray-700 hover:bg-purple-50 transition-colors cursor-pointer"
                  type="button"
                  aria-label="Toggle menu"
                >
                  {isOpen ? <X size={22} /> : <Menu size={22} />}
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Categories Navigation - Desktop only (Stays visible on scroll) */}
        <div
          className={`hidden lg:block bg-white transition-all duration-200 ${!isVisible ? "border-b border-purple-100 shadow-lg shadow-purple-500/10 bg-white/95 backdrop-blur-md py-2.5 sm:py-3" : "border-gray-200 border-t shadow-xs py-0.5"}`}
        >
          <div
            data-categories-menu
            className="relative mx-auto px-6 lg:px-8 max-w-7xl"
            onMouseLeave={() => {
              setShowCategories(false);
              setActiveCategory(categories[0]?._id ?? null);
            }}
          >
            <div
              className={`flex items-center gap-3.5 xl:gap-6 2xl:gap-8 transition-all duration-200 ${!isVisible ? "h-14 sm:h-16" : "h-11 sm:h-12"}`}
            >
              {/* All Categories Dropdown Trigger */}
              <button
                onMouseEnter={() => {
                  setShowCategories(true);
                  setActiveCategory(categories[0]?._id ?? null);
                }}
                className={`flex items-center cursor-pointer gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 rounded-lg font-semibold text-white transition-all flex-shrink-0 shadow-sm shadow-purple-500/20 active:scale-98 ${
                  !isVisible
                    ? "px-4 py-2.5 text-sm"
                    : "px-3.5 py-2 text-xs sm:text-sm"
                }`}
              >
                <Menu size={16} />
                Browse Categories
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${showCategories ? "rotate-180" : ""}`}
                />
              </button>

              {/* Divider */}
              <div
                className={`w-px bg-gray-200 flex-shrink-0 transition-all ${!isVisible ? "h-6" : "h-4"}`}
              />

              {/* Quick Links - Desktop */}
              {[
                { label: "Home", href: "/shop" },
                { label: "Trending", href: "/shop/trending" },
                { label: "New Arrivals", href: "/shop/new-arrivals" },
                { label: "Best Sellers", href: "/shop/best-sellers" },
                { label: "Special Deals", href: "/shop/deals" },
                { label: "About Us", href: "/shop/about-us" },
                { label: "Contact Us", href: "/shop/contact-us" },
              ].map((link) => {
                const isActive =
                  pathname === link.href ||
                  (link.href !== "/shop" && pathname.startsWith(link.href));
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="relative flex flex-col items-center group"
                  >
                    <motion.span
                      whileHover={{ y: -1 }}
                      className={`font-semibold transition-colors cursor-pointer whitespace-nowrap px-1 ${
                        !isVisible
                          ? "text-sm sm:text-[15px]"
                          : "text-xs sm:text-sm"
                      } ${
                        isActive
                          ? "text-purple-600"
                          : "text-gray-600 hover:text-purple-600"
                      }`}
                    >
                      {link.label}
                    </motion.span>
                    {/* Active underline indicator */}
                    <span
                      className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-200 ${
                        isActive
                          ? "w-full"
                          : "w-0 group-hover:w-3/4 group-hover:opacity-50"
                      }`}
                    />
                  </Link>
                );
              })}
            </div>

            {/* Mega Menu — spans full container width */}
            <AnimatePresence>
              {showCategories && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  onMouseEnter={() => setShowCategories(true)}
                  className="left-0 right-0 absolute top-full bg-white shadow-2xl border border-gray-100 rounded-b-2xl overflow-hidden z-50"
                >
                  <div className="flex" style={{ minHeight: 320 }}>
                    {/* Categories List */}
                    <div
                      className="bg-gray-50 border-r border-gray-100 w-56 flex-shrink-0 overflow-y-auto"
                      style={{ maxHeight: 480 }}
                    >
                      {categories.length === 0 ? (
                        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                          No categories yet
                        </div>
                      ) : (
                        categories.map((category) => (
                          <Link
                            key={category._id}
                            href={`/shop/${category.slug}`}
                          >
                            <div
                              onMouseEnter={() =>
                                setActiveCategory(category._id)
                              }
                              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-gray-100/60 ${
                                activeCategory === category._id
                                  ? "bg-white text-purple-600 border-l-2 border-l-purple-500"
                                  : "text-gray-700 hover:bg-white hover:text-purple-600"
                              }`}
                            >
                              <div className="w-8 h-8 rounded-lg overflow-hidden border border-gray-100 bg-white flex-shrink-0">
                                {category.image ? (
                                  <img
                                    src={
                                      getValidImageUrl(category.image) ||
                                      undefined
                                    }
                                    alt={category.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-purple-50 text-purple-400">
                                    <Package size={14} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">
                                  {category.name}
                                </p>
                                <p className="text-gray-400 text-[10px]">
                                  {category.subCategories?.length || 0}{" "}
                                  subcategories
                                </p>
                              </div>
                              {activeCategory === category._id && (
                                <ChevronDown
                                  size={12}
                                  className="-rotate-90 text-purple-400 flex-shrink-0"
                                />
                              )}
                            </div>
                          </Link>
                        ))
                      )}
                    </div>

                    {/* Subcategories Panel */}
                    <div className="flex-1 p-6 bg-white overflow-hidden">
                      {activeCategory ? (
                        <div>
                          <div className="flex items-center justify-between mb-5">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">
                                {
                                  categories.find(
                                    (c) => c._id === activeCategory,
                                  )?.name
                                }
                              </h3>
                              <p className="text-gray-400 text-xs mt-0.5">
                                Browse subcategories
                              </p>
                            </div>
                            <Link
                              href={`/shop/${categories.find((c) => c._id === activeCategory)?.slug}`}
                              className="text-purple-600 font-semibold hover:text-pink-600 transition-colors text-xs border border-purple-200 px-3 py-1.5 rounded-lg hover:bg-purple-50"
                            >
                              View All →
                            </Link>
                          </div>

                          <div className="grid grid-cols-3 xl:grid-cols-4 gap-3">
                            {categories
                              .find((c) => c._id === activeCategory)
                              ?.subCategories?.map((sub) => (
                                <Link
                                  key={sub._id}
                                  href={`/shop/${categories.find((c) => c._id === activeCategory)?.slug}/${sub.slug}`}
                                >
                                  <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    className="group flex items-center gap-2.5 bg-gray-50 hover:bg-purple-50 hover:shadow-sm p-3 rounded-lg transition-all cursor-pointer border border-transparent hover:border-purple-100"
                                  >
                                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-white flex-shrink-0 group-hover:border-purple-200 transition-colors">
                                      {sub.image ? (
                                        <img
                                          src={
                                            getValidImageUrl(sub.image) ||
                                            undefined
                                          }
                                          alt={sub.name}
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400 group-hover:bg-purple-50 group-hover:text-purple-400 transition-colors">
                                          <Package size={18} />
                                        </div>
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-semibold text-gray-800 group-hover:text-purple-600 transition-colors text-sm truncate">
                                        {sub.name}
                                      </p>
                                      <p className="text-gray-400 text-[11px] font-medium mt-0.5">
                                        {sub.productCount ?? 0}{" "}
                                        {sub.productCount === 1
                                          ? "Product"
                                          : "Products"}
                                      </p>
                                    </div>
                                  </motion.div>
                                </Link>
                              ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col justify-center items-center h-full text-gray-300 gap-3">
                          <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center">
                            <Menu size={24} className="opacity-40" />
                          </div>
                          <p className="text-sm font-medium text-gray-400">
                            Hover a category to explore
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile & Tablet Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden bg-white border-gray-200 border-t w-full overflow-y-auto origin-top shadow-lg"
              style={{ maxHeight: "calc(100vh - 140px)" }}
            >
              {/* Mobile Search */}
              <div className="p-3 sm:p-4 border-gray-100 border-b">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="py-2.5 pr-3 pl-9 border border-gray-300 focus:border-purple-500 rounded-lg focus:outline-none w-full text-sm transition-colors"
                  />
                  <Search
                    className="top-1/2 left-3 absolute text-gray-400 -translate-y-1/2"
                    size={16}
                  />
                </div>
              </div>

              {/* Mobile User Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 sm:p-4 border-gray-100 border-b">
                {user ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex justify-center items-center bg-gradient-to-br from-purple-600 to-pink-600 rounded-full w-10 h-10 sm:w-12 sm:h-12 overflow-hidden flex-shrink-0">
                        {user?.profileImage ? (
                          <img
                            src={
                              getValidImageUrl(user.profileImage) || undefined
                            }
                            alt={user?.name}
                            className="rounded-full w-full h-full object-cover"
                          />
                        ) : (
                          <User className="text-white" size={20} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 text-sm truncate">
                          {user?.name}
                        </p>
                        <p className="text-gray-600 text-xs truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Link href="/profile" onClick={() => setIsOpen(false)}>
                        <motion.button
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 }}
                          className="flex cursor-pointer items-center gap-2 hover:bg-white px-3 py-2 rounded-lg w-full text-gray-700 text-left font-medium text-sm transition-colors"
                        >
                          <User size={16} />
                          My Profile
                        </motion.button>
                      </Link>
                      <Link href="/orders" onClick={() => setIsOpen(false)}>
                        <motion.button
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.15 }}
                          className="flex cursor-pointer items-center gap-2 hover:bg-white px-3 py-2 rounded-lg w-full text-gray-700 text-left font-medium text-sm transition-colors"
                        >
                          <Package size={16} />
                          My Orders
                        </motion.button>
                      </Link>
                      <Link href="/wishlist" onClick={() => setIsOpen(false)}>
                        <motion.button
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                          className="flex cursor-pointer items-center gap-2 hover:bg-white px-3 py-2 rounded-lg w-full text-gray-700 text-left font-medium text-sm transition-colors"
                        >
                          <Heart size={16} />
                          Wishlist ({wishlistCount})
                        </motion.button>
                      </Link>
                      <Link href="/settings" onClick={() => setIsOpen(false)}>
                        <motion.button
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.25 }}
                          className="flex cursor-pointer items-center gap-2 hover:bg-white px-3 py-2 rounded-lg w-full text-gray-700 text-left font-medium text-sm transition-colors"
                        >
                          <Settings size={16} />
                          Settings
                        </motion.button>
                      </Link>
                    </div>
                  </motion.div>
                ) : (
                  <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    onClick={() => {
                      setIsOpen(false);
                      setAuthDrawerMode("login");
                      setIsAuthDrawerOpen(true);
                    }}
                    className="flex cursor-pointer items-center gap-2 py-2 text-purple-600 font-bold text-sm w-full text-left"
                  >
                    <User size={20} />
                    Login / Sign up
                  </motion.button>
                )}
              </div>

              {/* Mobile Categories Accordion */}
              <div className="p-3 sm:p-4 border-gray-100 border-b">
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  onClick={() => setShowMobileCategories(!showMobileCategories)}
                  className="flex items-center justify-between w-full pb-3 font-bold text-gray-800"
                >
                  <div className="flex items-center gap-2">
                    <Grid3x3 size={18} />
                    Browse Categories
                  </div>
                  <ChevronDown
                    size={20}
                    className={`transition-transform duration-300 ${
                      showMobileCategories ? "rotate-180" : ""
                    }`}
                  />
                </motion.button>

                <AnimatePresence>
                  {showMobileCategories && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-2 pt-3"
                    >
                      {categories.map((category, index) => (
                        <Link
                          key={category._id}
                          href={`/shop/${category.slug}`}
                          onClick={() => {
                            setIsOpen(false);
                            setShowMobileCategories(false);
                          }}
                        >
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-gray-50 hover:bg-purple-50 px-3 py-2.5 rounded-lg font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer text-sm"
                          >
                            <div className="flex items-center justify-between">
                              <span>{category.name}</span>
                              <span className="text-[11px] text-purple-600 bg-purple-100/60 px-2 py-0.5 rounded-md font-semibold">
                                {category.subCategories?.length || 0}{" "}
                                subcategories
                              </span>
                            </div>
                          </motion.div>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Shop Navigation Links */}
              <div className="p-3 sm:p-4 space-y-1 border-gray-100 border-b">
                {[
                  { label: "Home", href: "/shop", delay: 0.15 },
                  { label: "Trending", href: "/shop/trending", delay: 0.2 },
                  {
                    label: "New Arrivals",
                    href: "/shop/new-arrivals",
                    delay: 0.25,
                  },
                  {
                    label: "Best Sellers",
                    href: "/shop/best-sellers",
                    delay: 0.3,
                  },
                  { label: "Special Deals", href: "/shop/deals", delay: 0.35 },
                  { label: "About Us", href: "/shop/about-us", delay: 0.4 },
                  {
                    label: "Contact Us",
                    href: "/shop/contact-us",
                    delay: 0.45,
                  },
                ].map((link) => {
                  const isActive =
                    pathname === link.href ||
                    (link.href !== "/shop" && pathname.startsWith(link.href));
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsOpen(false)}
                    >
                      <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: link.delay }}
                        className={`flex cursor-pointer items-center gap-2 px-3 py-2.5 rounded-lg w-full text-left font-medium text-sm transition-all ${
                          isActive
                            ? "bg-purple-50 text-purple-700 font-semibold border-l-2 border-purple-600 pl-3"
                            : "hover:bg-purple-50 text-gray-700 hover:text-purple-700"
                        }`}
                      >
                        {isActive && (
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-600 shrink-0" />
                        )}
                        {link.label}
                      </motion.button>
                    </Link>
                  );
                })}
              </div>

              {/* Mobile Additional Links */}
              <div className="p-3 sm:p-4 space-y-2 border-gray-100 border-b">
                <Link href="/track-order" onClick={() => setIsOpen(false)}>
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex cursor-pointer items-center gap-2 hover:bg-purple-50 px-3 py-2.5 rounded-lg w-full text-gray-700 text-left font-medium text-sm transition-colors"
                  >
                    Track Order
                  </motion.button>
                </Link>
                <Link href="/help" onClick={() => setIsOpen(false)}>
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45 }}
                    className="flex cursor-pointer items-center gap-2 hover:bg-purple-50 px-3 py-2.5 rounded-lg w-full text-gray-700 text-left font-medium text-sm transition-colors"
                  >
                    Get Help
                  </motion.button>
                </Link>
              </div>

              {/* Mobile Logout */}
              {user && (
                <div className="p-3 sm:p-4 border-gray-100 border-t">
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    onClick={handleLogoutConfirm}
                    className="flex cursor-pointer items-center gap-2 hover:bg-red-50 px-3 py-2.5 rounded-lg w-full text-red-600 text-left font-medium text-sm transition-colors"
                  >
                    <LogOut size={16} />
                    Logout
                  </motion.button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Auth Sidebar Drawer (Login / Register / Forgot Password) — rendered OUTSIDE the transformed header so `position: fixed` measures against the real viewport */}
      <AuthDrawer
        isOpen={isAuthDrawerOpen}
        onClose={() => setIsAuthDrawerOpen(false)}
        defaultMode={authDrawerMode}
      />
    </>
  );
};

export default ShopHeader;
